import { useCallback, useEffect, useRef, useState } from 'react';
import type { TwoStepStatus } from 'zitimail-shared-types';

import { api, describeError } from '../admin/api';

/**
 * Two-step sign-in (ADR-0058): an authenticator app's code after the
 * password, and recovery codes for a lost phone.
 *
 * Every change happens on Keycloak's own pages. The buttons here only open
 * them, through the BFF's `/auth/action`, which allows nothing but these
 * actions; Keycloak asks for the person's sign-in again when it is not
 * recent, and asks before it removes anything. Webmail comes back to
 * Settings with `?two-step=done|cancelled|failed`.
 */

const SET_UP = 'CONFIGURE_TOTP';
const CODES = 'CONFIGURE_RECOVERY_AUTHN_CODES';

/** Fewer left than this and the person is told to make new ones (Keycloak's own warning is at 4). */
const FEW_CODES = 4;

/** Where Keycloak's page for `action` (then `then`) is opened from. */
export function actionUrl(action: string, then?: string): string {
  const query = new URLSearchParams({ kc_action: action });
  if (then) {
    query.set('then', then);
  }
  return `/auth/action?${query}`;
}

export type Outcome = 'done' | 'cancelled' | 'failed';

/** How the last trip to Keycloak's page went, if webmail just came back from one. */
export function twoStepOutcome(): Outcome | null {
  const found = new URLSearchParams(window.location.search).get('two-step');
  return found === 'done' || found === 'cancelled' || found === 'failed' ? found : null;
}

const OUTCOME_TEXT: Record<Outcome, { ok: boolean; text: string }> = {
  done: { ok: true, text: 'Done. Your two-step sign-in is up to date.' },
  cancelled: { ok: true, text: 'Nothing was changed.' },
  failed: { ok: false, text: 'That did not finish. Try again.' },
};

/** The desktop app has no `/auth/action`: its sign-in is the app's own. */
function inDesktopApp(): boolean {
  return '__TAURI_INTERNALS__' in window;
}

function codesLine(codes: NonNullable<TwoStepStatus['recovery_codes']>): string {
  if (codes.remaining !== null && codes.total !== null) {
    return `saved, ${codes.remaining} of ${codes.total} left`;
  }
  return 'saved';
}

/** The Settings section. */
export function TwoStepSignIn({
  tenant,
  navigate = (url: string) => window.location.assign(url),
}: {
  tenant: string;
  /** Opens Keycloak's page; replaced in tests. */
  navigate?: (url: string) => void;
}): JSX.Element | null {
  const route = `/tenants/${encodeURIComponent(tenant)}/my-two-step`;
  const [status, setStatus] = useState<TwoStepStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [outcome] = useState<Outcome | null>(twoStepOutcome);
  const ref = useRef<HTMLElement>(null);

  const reload = useCallback(() => {
    api<TwoStepStatus>(route)
      .then((found) => {
        setStatus(found);
        setError(null);
      })
      .catch((e: unknown) => setError(describeError(e)));
  }, [route]);
  useEffect(reload, [reload]);

  // Back from Keycloak: say how it went, here, and forget the address bar's
  // note so a reload does not say it again.
  useEffect(() => {
    if (!outcome) {
      return;
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('two-step');
    window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
  }, [outcome]);
  useEffect(() => {
    if (outcome && status) {
      ref.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [outcome, status]);

  if (!status) {
    return error ? (
      <section className="panel" aria-label="Two-step sign-in">
        <h3>Two-step sign-in</h3>
        <p className="error">{error}</p>
      </section>
    ) : null;
  }
  if (!status.has_sign_in) {
    return null;
  }
  if (status.provider_sign_in) {
    return (
      <section className="panel" aria-label="Two-step sign-in">
        <h3>Two-step sign-in</h3>
        <p className="muted">
          You sign in through your organisation&apos;s own sign-in service. It decides whether you
          give a second step, and you set that up there.
        </p>
      </section>
    );
  }

  const authenticators = status.authenticators;
  const on = authenticators.length > 0;
  const codes = status.recovery_codes;
  const offered = status.recovery_codes_offered;
  const desktop = inDesktopApp();
  const open = (action: string, then?: string) => navigate(actionUrl(action, then));
  const first = authenticators[0];

  return (
    <section className="panel" aria-label="Two-step sign-in" ref={ref}>
      <h3>Two-step sign-in</h3>
      <p className="muted">
        After your password, a 6-digit code from an authenticator app on your phone, such as Google
        Authenticator or Microsoft Authenticator.
        {offered && ' Recovery codes let you sign in if you lose the phone; each works once.'}
      </p>
      {status.required && <p>Your organisation requires two-step sign-in.</p>}
      {outcome && (
        <p className={OUTCOME_TEXT[outcome].ok ? 'ok' : 'error'}>{OUTCOME_TEXT[outcome].text}</p>
      )}

      <ul className="two-step-state">
        <li>
          Authenticator app:{' '}
          <strong>
            {on ? authenticators.map((a) => a.label ?? 'authenticator').join(', ') : 'not set up'}
          </strong>
        </li>
        {offered && (
          <li>
            Recovery codes: <strong>{codes ? codesLine(codes) : 'not saved'}</strong>
          </li>
        )}
      </ul>

      {!on && codes && offered && (
        <p className="error">
          Your recovery codes are your only second step now, so every sign-in asks for one. Set up
          an authenticator app.
        </p>
      )}
      {on && codes && offered && codes.remaining !== null && codes.remaining < FEW_CODES && (
        <p className="error">
          {codes.remaining === 0
            ? 'You have no recovery codes left. Make new ones.'
            : `Only ${codes.remaining} recovery ${codes.remaining === 1 ? 'code' : 'codes'} left. Make new ones.`}
        </p>
      )}
      {on && !codes && offered && (
        <p>
          Save recovery codes so a lost phone does not lock you out. Keep them somewhere safe, away
          from the phone.
        </p>
      )}

      {desktop ? (
        <p className="muted">Change these in webmail, in your browser.</p>
      ) : (
        <div className="save-row">
          {!on && (
            <button
              type="button"
              className="btn"
              style={{ width: 'auto' }}
              // New codes come straight after the authenticator; someone who
              // already has codes keeps them.
              onClick={() => open(SET_UP, offered && !codes ? CODES : undefined)}
            >
              {codes ? 'Set up an authenticator app' : 'Turn on'}
            </button>
          )}
          {on && offered && (
            <button
              type="button"
              className={codes ? 'btn ghost' : 'btn'}
              style={{ width: 'auto' }}
              onClick={() => {
                if (
                  !codes ||
                  window.confirm(
                    'Make new recovery codes? The codes you saved before stop working.',
                  )
                ) {
                  open(CODES);
                }
              }}
            >
              {codes ? 'Make new recovery codes' : 'Create recovery codes'}
            </button>
          )}
          {on && first && authenticators.length === 1 && (
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              onClick={() => {
                if (
                  window.confirm(
                    offered
                      ? 'Replace your authenticator? You set up the new phone first, then the old one stops working, and you save a new set of recovery codes.'
                      : 'Replace your authenticator? You set up the new phone first; the old one stops working after that.',
                  )
                ) {
                  // New codes last: Keycloak's sign-in opens on the oldest
                  // second step, and it should be the authenticator.
                  const remove = `delete_credential:${first.id}`;
                  open(SET_UP, offered ? `${remove},${CODES}` : remove);
                }
              }}
            >
              Replace my authenticator
            </button>
          )}
        </div>
      )}

      {!desktop && authenticators.length > 1 && (
        <div className="two-step-list">
          <p className="muted">
            You have more than one authenticator. Remove any you no longer use.
          </p>
          {authenticators.map((a) => (
            <div key={a.id} className="save-row">
              <span>{a.label ?? 'Authenticator'}</span>
              <button
                type="button"
                className="btn ghost"
                style={{ width: 'auto', marginLeft: 'auto' }}
                onClick={() => open(`delete_credential:${a.id}`)}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

const DISMISSED = 'zitimail.two-step-reminder.dismissed';

function dismissedFor(email: string): boolean {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISSED) ?? '[]') as unknown;
    return Array.isArray(stored) && stored.includes(email.toLowerCase());
  } catch {
    return false;
  }
}

function dismiss(email: string): void {
  try {
    const stored = JSON.parse(localStorage.getItem(DISMISSED) ?? '[]') as unknown;
    const list = Array.isArray(stored) ? stored.filter((x) => typeof x === 'string') : [];
    localStorage.setItem(DISMISSED, JSON.stringify([...new Set([...list, email.toLowerCase()])]));
  } catch {
    // Private browsing: it shows again next time, which is harmless.
  }
}

/**
 * A note for a person with an authenticator and no recovery codes, until they
 * save some or say not now; "not now" is remembered per person in this
 * browser.
 */
export function TwoStepReminder({
  tenant,
  email,
  onOpen,
}: {
  tenant: string;
  email: string;
  /** Opens Settings. */
  onOpen: () => void;
}): JSX.Element | null {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (dismissedFor(email)) {
      return undefined;
    }
    const controller = new AbortController();
    api<TwoStepStatus>(`/tenants/${encodeURIComponent(tenant)}/my-two-step`, {
      signal: controller.signal,
    })
      .then((s) =>
        setShown(
          s.has_sign_in &&
            !s.provider_sign_in &&
            s.recovery_codes_offered &&
            s.authenticators.length > 0 &&
            s.recovery_codes === null,
        ),
      )
      // Not knowing is no reason to nag.
      .catch(() => setShown(false));
    return () => controller.abort();
  }, [tenant, email]);

  if (!shown) {
    return null;
  }
  const close = () => {
    dismiss(email);
    setShown(false);
  };
  return (
    <section className="reminders two-step-reminder" aria-label="Two-step sign-in" role="status">
      <article className="reminder">
        <span className="reminder-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 3 4 6v6c0 4.4 3.4 8.3 8 9 4.6-.7 8-4.6 8-9V6l-8-3Z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        </span>
        <div className="reminder-text">
          <strong>Save your recovery codes</strong>
          <span className="reminder-times">
            Save recovery codes so a lost phone does not lock you out.
          </span>
        </div>
        <div className="reminder-actions">
          <button
            type="button"
            onClick={() => {
              // Shown again next time until the codes are saved.
              setShown(false);
              onOpen();
            }}
          >
            Open Settings
          </button>
          <button type="button" onClick={close}>
            Not now
          </button>
        </div>
      </article>
    </section>
  );
}
